import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Row,
  useReactTable,
} from "@tanstack/react-table";
import type React from "react";
import styles from "./index.module.scss";

// CustomColumnDef は不要になったため削除

interface TableComponentProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[]; // 元の ColumnDef<TData>[] に戻す
  updateData: (rowIndex: number, columnId: keyof TData, value: string) => void;
  isEditable: boolean;
  onRowClick?: (row: Row<TData>) => void;
}

const CustomizeSpecificationTable = <TData extends object>({
  data,
  columns, // 型は ColumnDef<TData>[]
  updateData,
  isEditable,
  onRowClick,
}: TableComponentProps<TData>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const isUrl = (value: string) => {
    try {
      new URL(value);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <table className={styles["table"]}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              // meta へのアクセス方法を修正 (?.width)
              <th key={header.id} style={{ width: header.column.columnDef.meta?.width }}>
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, rowIndex) => (
          <tr
            key={row.id}
            onClick={() => !isEditable && onRowClick?.(row)}
            className={`${styles["tr"]} ${!isEditable && onRowClick ? styles["clickableRow"] : ""}`}
          >
            {row.getVisibleCells().map((cell) => {
              // columnDef の型アサーションを削除
              const columnDef = cell.column.columnDef;
              const context = cell.getContext();
              let cellContent: React.ReactNode;

              // 特定のIDやcell関数を持つセルの処理
              if (columnDef.cell && (cell.column.id === "detail" || cell.column.id === "delete")) {
                cellContent = flexRender(columnDef.cell, context);
              }
              // 編集モードかつ特定のIDでないセルはtextareaとして表示
              else if (isEditable && cell.column.id !== "detail" && cell.column.id !== "delete") {
                cellContent = (
                  <textarea
                    value={(cell.getValue() as string | number) || ""}
                    onChange={(e) =>
                      updateData(rowIndex, cell.column.id as keyof TData, e.target.value)
                    }
                    style={{ width: "100%", resize: "none" }}
                  />
                );
              }
              // URLの場合はリンクとして表示
              else if (typeof cell.getValue() === "string" && isUrl(cell.getValue() as string)) {
                cellContent = (
                  <div
                    className={styles["link"]}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(cell.getValue() as string, "_blank");
                    }}
                    style={{ cursor: "pointer", color: "blue" }}
                  >
                    {cell.getValue() as string}
                  </div>
                );
              }
              // それ以外は通常のテキストとして表示
              else {
                cellContent = flexRender(columnDef.cell ?? cell.column.id, context);
              }

              // meta へのアクセス方法を修正 (?.width)
              return (
                <td key={cell.id} style={{ width: columnDef.meta?.width }}>
                  {cellContent}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CustomizeSpecificationTable;
